// apps/web/src/lib/auth.tsx
//
// Auth context. Provides the server-verified `me` payload to the
// entire app, plus a `refresh()` that re-fetches /me. The data flow:
//
//   1. On app load, `useMe()` is called by <RequireAuth> wrappers
//      and the top-level auth probe. /api/v1/user/me is the
//      load-bearing source of truth (phase-1-5 §1).
//   2. On 200, the user is in.
//   3. On 401, the user is out.
//   4. Cross-tab: when one tab logs out, others get a
//      BroadcastChannel message and re-probe /me to discover the
//      change.
//   5. Session-expired: apiClient dispatches a window event; we
//      re-probe /me (which 401s) to discover the change, then
//      components can show an interstitial or redirect.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { apiGet, publishAuthEvent, SESSION_EXPIRED_EVENT } from './apiClient.js';
import type { ApiError } from './apiClient.js';
import type { UserMe } from './api.js';

export type MeState =
  | { status: 'pending' }
  | { status: 'unauthenticated'; requestId: string | null }
  | { status: 'authenticated'; user: UserMe; requestId: string };

export interface AuthContextValue {
  me: MeState;
  refresh: () => Promise<void>;
  /** Force a re-probe (used after login / logout). */
  invalidate: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [me, setMe] = useState<MeState>({ status: 'pending' });
  // Use a ref to deduplicate concurrent /me calls — many route
  // guards may mount in parallel and all want to know "am I signed in?"
  const inflight = useRef<Promise<void> | null>(null);

  const probe = useCallback(async (): Promise<void> => {
    if (inflight.current) return inflight.current;
    const p = (async () => {
      setMe((prev) => prev.status === 'pending' ? prev : { status: 'pending' });
      try {
        const res = await apiGet<{ user: UserMe }>('/api/v1/user/me', {
          // Don't dispatch session-expired from this probe — the
          // route guards are the canonical place that handles "not
          // signed in".
          suppressSessionExpired: true,
        });
        setMe({ status: 'authenticated', user: res.data.user, requestId: res.requestId });
      } catch (err) {
        const apiErr = err as ApiError;
        const requestId = apiErr?.requestId ?? null;
        setMe({ status: 'unauthenticated', requestId });
      }
    })();
    inflight.current = p;
    try { await p; } finally { inflight.current = null; }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await probe();
  }, [probe]);

  const invalidate = useCallback((): void => {
    setMe({ status: 'pending' });
    void probe();
  }, [probe]);

  // Initial probe on mount.
  useEffect(() => {
    void probe();
  }, [probe]);

  // Cross-tab sync (phase-1-5 §9.1). Listen for:
  //   - 'logout' / 'logout-all': some other tab signed us out; re-probe.
  //   - session-expired event: apiClient already tried silent refresh
  //     and failed; re-probe to update our state.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('bumbum:auth');
    const onMessage = (ev: MessageEvent) => {
      if (ev.data && (ev.data.type === 'logout' || ev.data.type === 'logout-all' || ev.data.type === 'reauth')) {
        void probe();
      }
    };
    ch.addEventListener('message', onMessage);
    const onSessionExpired = () => { void probe(); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => {
      ch.removeEventListener('message', onMessage);
      ch.close();
      window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    };
  }, [probe]);

  return (
    <AuthContext.Provider value={{ me, refresh, invalidate }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Convenience: pull the UserMe iff authenticated. */
export function useMeOrNull(): UserMe | null {
  const { me } = useAuth();
  return me.status === 'authenticated' ? me.user : null;
}

/**
 * Publish a logout event. The caller is responsible for the actual
 * API call (POST /auth/logout or /auth/logout-all); this helper
 * just notifies other tabs and the AuthContext to re-probe.
 */
export function notifyLoggedOut(kind: 'logout' | 'logout-all' = 'logout'): void {
  publishAuthEvent(kind);
}
